import React, { FC, useState } from 'react';
import { View, Text, TextInput } from 'react-native';
import i18n from 'i18n-js';

import { scale as initialScale } from '@components/frame';
import { font as initialFont } from '@styles/fonts';
import TouchableElement from '@components/common/touchableElement';
import StepperButton, { StepperButtonType } from '@components/common/StepperButton';
import LinearProgress from '@components/common/LinearProgress';
import type { ProducedQuantitySectionProps } from '@v2/components/FinishJobModal/types';

const ProducedQuantitySection: FC<ProducedQuantitySectionProps> = ({
  producedQuantity,
  quantityTarget,
  quantityDelta,
  progressPercent,
  onIncrement,
  onDecrement,
  onSetDirect,
  scale = initialScale,
  font = initialFont,
  C,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(String(producedQuantity));

  const deltaColor =
    quantityDelta < 0 ? C('System/Light/Orange') : quantityDelta > 0 ? C('System/Light/Blue') : C('Label/Light/Tertiary');
  const deltaText =
    quantityDelta < 0
      ? `(${quantityDelta} ${i18n.t('pieces')})`
      : quantityDelta > 0
        ? `(+${quantityDelta} ${i18n.t('pieces')})`
        : '';

  const handleEditBlur = () => {
    setIsEditing(false);
    const parsed = parseInt(editValue, 10);
    if (!isNaN(parsed)) {
      onSetDirect(parsed);
    }
  };

  return (
    <View style={{ paddingHorizontal: scale(24), paddingBottom: scale(16) }}>
      <Text style={[font['Headline'], { color: C('Label/Light/Primary'), marginBottom: scale(16) }]}>
        {i18n.t('Produced Quantity')}
      </Text>

      {/* Stepper row */}
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: scale(24), marginBottom: scale(12) }}>
        <StepperButton type={StepperButtonType.DECREMENT} onPress={onDecrement} disabled={producedQuantity <= 0} scale={scale} C={C} />

        {/* Quantity display / edit */}
        {isEditing ? (
          <TextInput
            style={[
              font['Large Title'],
              {
                color: C('Label/Light/Primary'),
                textAlign: 'center',
                minWidth: scale(80),
                borderBottomWidth: 2,
                borderBottomColor: C('System/Light/Blue'),
                paddingVertical: scale(4),
                outline: 'none',
              },
            ]}
            value={editValue}
            onChangeText={setEditValue}
            onBlur={handleEditBlur}
            autoFocus
            keyboardType="numeric"
          />
        ) : (
          <TouchableElement
            onPress={() => {
              setEditValue(String(producedQuantity));
              setIsEditing(true);
            }}
          >
            {({ boxStyle }) => (
              <View style={[boxStyle, { minWidth: scale(80), alignItems: 'center' }]}>
                <Text style={[font['Large Title'], { color: C('Label/Light/Primary') }]}>{producedQuantity}</Text>
              </View>
            )}
          </TouchableElement>
        )}

        <StepperButton type={StepperButtonType.INCREMENT} onPress={onIncrement} scale={scale} C={C} />
      </View>

      {/* Planned info */}
      <View style={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: scale(6), marginBottom: scale(12) }}>
        <Text style={[font['Footnote'], { color: C('Label/Light/Tertiary') }]}>
          {i18n.t('Planned')}: {quantityTarget} {i18n.t('pieces')}
        </Text>
        {deltaText ? <Text style={[font['Footnote'], { color: deltaColor }]}>{deltaText}</Text> : null}
      </View>

      <View style={{ alignSelf: 'center' }}>
        <LinearProgress percent={progressPercent} fillColor="Label/Light/Primary" scale={scale} C={C} />
      </View>

    </View>
  );
};

export default ProducedQuantitySection;
